import {computed, Inject, Injectable, Optional, signal} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import type {Observable} from 'rxjs';
import type {LmdbServerOption} from '../config/lmdb-cache-config';
import {LMDB_HTTP_GET, LMDB_HTTP_POST} from '../tokens/lmdb-http.token';
import type {LmdbKeyDetailRequest, LmdbKeysListRequest, LmdbPostRequestBody} from '../models/lmdb-admin-request.model';

export interface StoreInfo {
  storeId: string;
  label: string;
  path: string;
  healthy: boolean;
  description?: string;
  totalKeyCount?: number;
}

export interface DatabaseInfo {
  name: string;
  description?: string;
  keyCount?: number;
}

export interface StoreStats {
  storeId: string;
  path: string;
  databaseCount: number;
  databases: { name: string; entryCount: number }[];
}

export interface KeyListResult {
  keys: string[];
  total?: number;
}

export interface StatisticsStoreDb {
  name: string;
  keyCount: number;
  sizeBytes: number;
}

export interface StatisticsStore {
  storeId: string;
  storeName: string;
  totalDatabases: number;
  totalKeys: number;
  totalSizeBytes: number;
  mapSizeLimitBytes: number;
  databases: StatisticsStoreDb[];
  shardId?: string;
  shardPath?: string;
}

export interface StatisticsResponse {
  totalStores: number;
  totalDatabases: number;
  totalKeys: number;
  totalSizeBytes: number;
  stores: StatisticsStore[];
  ariShards?: StatisticsStore[];
}

/** From lmdb-cache-admin: {@code ARI_LMDB_SHARD_PATHS_JSON} (same JSON as ari-processing). */
export interface AriShardInfo {
  shardId: string;
  path: string;
  label: string;
  healthy: boolean;
  totalKeyCount: number;
}

@Injectable({providedIn: 'root'})
export class LmdbAdminApiService {
  private readonly selectedServer = signal<LmdbServerOption | null>(null);
  private readonly stores = signal<StoreInfo[]>([]);
  private readonly databases = signal<DatabaseInfo[]>([]);
  private readonly keysResult = signal<KeyListResult>({keys: [], total: 0});
  private readonly selectedValue = signal<unknown>(null);
  private readonly storeStats = signal<StoreStats | null>(null);
  private readonly statistics = signal<StatisticsResponse | null>(null);
  /** ARI shard roots from the server env (empty when unset or not loaded). */
  private readonly ariShards = signal<AriShardInfo[]>([]);
  /** True after the latest {@link loadAriShards} attempt finished (success or error). */
  private readonly ariShardsLoaded = signal(false);
  private readonly loading = signal(false);
  private readonly error = signal<string | null>(null);
  /** Absolute LMDB root for the selected ARI shard (matches `ariLmdbRootPath` / ecosystem node paths). */
  private readonly selectedAriShardPath = signal<string | null>(null);

  readonly selectedServerSignal = this.selectedServer.asReadonly();
  readonly statisticsSignal = this.statistics.asReadonly();
  readonly ariShardsSignal = this.ariShards.asReadonly();
  readonly ariShardsLoadedSignal = this.ariShardsLoaded.asReadonly();
  readonly storesSignal = this.stores.asReadonly();
  readonly databasesSignal = this.databases.asReadonly();
  readonly keysResultSignal = this.keysResult.asReadonly();
  readonly selectedValueSignal = this.selectedValue.asReadonly();
  readonly storeStatsSignal = this.storeStats.asReadonly();
  readonly loadingSignal = this.loading.asReadonly();
  readonly errorSignal = this.error.asReadonly();
  readonly selectedAriShardPathSignal = this.selectedAriShardPath.asReadonly();

  readonly apiBaseUrl = computed(() => {
    const s = this.selectedServer();
    return s ? s.apiBaseUrl.replace(/\/$/, '') : '';
  });

  constructor(
    private readonly http: HttpClient,
    @Optional() @Inject(LMDB_HTTP_GET) private readonly lmdbHttpGet: ((path: string, baseUrl: string) => Observable<unknown>) | null,
    @Optional() @Inject(LMDB_HTTP_POST) private readonly lmdbHttpPost: ((path: string, baseUrl: string, body: LmdbPostRequestBody) => Observable<object>) | null,
  ) {
  }

  setSelectedServer(server: LmdbServerOption): void {
    this.selectedServer.set(server);
    this.error.set(null);
    this.selectedAriShardPath.set(null);
    this.ariShards.set([]);
    this.ariShardsLoaded.set(false);
  }

  /** Selected ARI LMDB root directory, or null when not using a shard / not on ARI store. */
  setSelectedAriShardPath(path: string | null): void {
    const next = path?.trim() || null;
    this.selectedAriShardPath.set(next);
  }

  private getUrl(path: string): string {
    const base = this.apiBaseUrl();
    if (!base) return '';
    return `${base}/${path.replace(/^\//, '')}`;
  }

  private getRequest<T>(path: string): Observable<T> | null {
    const base = this.apiBaseUrl();
    if (!base) return null;
    const normalizedPath = path.replace(/^\//, '');
    if (this.lmdbHttpGet) {
      return this.lmdbHttpGet(normalizedPath, base) as Observable<T>;
    }
    return this.http.get<T>(this.getUrl(path));
  }

  private postRequest<T>(path: string, body: LmdbPostRequestBody): Observable<T> | null {
    const base = this.apiBaseUrl();
    if (!base) return null;
    const normalizedPath = path.replace(/^\//, '');
    if (this.lmdbHttpPost) {
      return this.lmdbHttpPost(normalizedPath, base, body) as Observable<T>;
    }
    return this.http.post<T>(this.getUrl(path), body);
  }

  private deleteRequest<T>(path: string): Observable<T> | null {
    const url: string = this.getUrl(path);
    if (!url) return null;
    return this.http.delete<T>(url);
  }

  /**
   * ARI shard root for POST request bodies; empty string when not applicable.
   */
  private ariLmdbRootPathForBody(storeId: string): string {
    if (storeId !== 'ari') {
      return '';
    }
    return this.selectedAriShardPath()?.trim() ?? '';
  }

  /**
   * Appends `ariLmdbRootPath` when inspecting the `ari` store and a shard path is selected.
   */
  private withAriShardQuery(storeId: string, relativePath: string): string {
    if (storeId !== 'ari') {
      return relativePath;
    }
    const shard = this.selectedAriShardPath();
    if (!shard) {
      return relativePath;
    }
    const sep = relativePath.includes('?') ? '&' : '?';
    return `${relativePath}${sep}ariLmdbRootPath=${encodeURIComponent(shard)}`;
  }

  loadStores(): void {
    const req = this.getRequest<StoreInfo[]>('lmdb-admin/stores');
    if (!req) {
      this.error.set('Select a server first');
      this.ariShards.set([]);
      this.ariShardsLoaded.set(true);
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.stores.set([]);
    this.ariShards.set([]);
    this.ariShardsLoaded.set(false);
    this.databases.set([]);
    this.keysResult.set({keys: [], total: 0});
    this.selectedValue.set(null);
    this.storeStats.set(null);

    req.subscribe({
      next: (list) => {
        this.stores.set(list ?? []);
        this.loading.set(false);
        this.loadAriShards();
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load stores');
        this.loading.set(false);
        this.ariShards.set([]);
        this.ariShardsLoaded.set(true);
      },
    });
  }

  /** Loads ARI shard paths from the admin host (ARI or LMDB shard map env — see backend). */
  loadAriShards(): void {
    const req = this.getRequest<AriShardInfo[]>('lmdb-admin/ari-shards');
    if (!req) {
      this.ariShards.set([]);
      this.ariShardsLoaded.set(true);
      return;
    }
    req.subscribe({
      next: (list) => {
        this.ariShards.set(Array.isArray(list) ? list : []);
        this.ariShardsLoaded.set(true);
      },
      error: () => {
        this.ariShards.set([]);
        this.ariShardsLoaded.set(true);
      },
    });
  }

  loadDatabases(storeId: string): void {
    let path: string = `lmdb-admin/stores/${encodeURIComponent(storeId)}/databases`;
    path = this.withAriShardQuery(storeId, path);
    const req = this.getRequest<DatabaseInfo[]>(path);
    if (!req) return;
    this.loading.set(true);
    this.error.set(null);
    req.subscribe({
      next: (list) => {
        this.databases.set(list ?? []);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load databases');
        this.loading.set(false);
      },
    });
  }

  loadKeys(
    storeId: string,
    dbName: string,
    prefix?: string,
    limit: number = 50,
    offset: number = 0,
  ): void {
    const path: string =
      `lmdb-admin/stores/${encodeURIComponent(storeId)}/databases/keys`;
    const body: LmdbKeysListRequest = {
      dbName,
      limit,
      offset,
      prefix: prefix ?? '',
      ariLmdbRootPath: this.ariLmdbRootPathForBody(storeId),
    };
    const req = this.postRequest<KeyListResult>(path, body);
    if (!req) return;
    this.loading.set(true);
    this.error.set(null);
    req.subscribe({
      next: (result: KeyListResult) => {
        const raw: KeyListResult = result ?? {keys: [], total: 0};
        const keys: string[] = Array.isArray(raw.keys) ? raw.keys : [];
        const total: number = typeof raw.total === 'number' ? raw.total : keys.length;
        this.keysResult.set({keys, total});
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load keys');
        this.loading.set(false);
      },
    });
  }

  loadValue(storeId: string, dbName: string, key: string): void {
    const path: string =
      `lmdb-admin/stores/${encodeURIComponent(storeId)}/databases/keys/detail`;
    const body: LmdbKeyDetailRequest = {
      dbName,
      key,
      ariLmdbRootPath: this.ariLmdbRootPathForBody(storeId),
    };
    const req = this.postRequest<object>(path, body);
    if (!req) return;
    this.loading.set(true);
    this.error.set(null);
    req.subscribe({
      next: (value) => {
        this.selectedValue.set(value);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load value');
        this.selectedValue.set(null);
        this.loading.set(false);
      },
    });
  }

  loadStoreStats(storeId: string): void {
    let path: string = `lmdb-admin/stores/${encodeURIComponent(storeId)}/stats`;
    path = this.withAriShardQuery(storeId, path);
    const req = this.getRequest<StoreStats>(path);
    if (!req) return;
    this.loading.set(true);
    this.error.set(null);
    req.subscribe({
      next: (stats) => {
        this.storeStats.set(stats ?? null);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load stats');
        this.storeStats.set(null);
        this.loading.set(false);
      },
    });
  }

  loadStatistics(): void {
    const req: Observable<StatisticsResponse> = this.getRequest<StatisticsResponse>('lmdb-admin/statistics');
    if (!req) return;
    this.error.set(null);
    req.subscribe({
      next: (data: StatisticsResponse) => this.statistics.set(data ?? null),
      error: (err) => {
        this.error.set(err?.message || 'Failed to load statistics');
        this.statistics.set(null);
      },
    });
  }

  deleteKey(storeId: string, dbName: string, key: string): Observable<{ deleted: boolean; error?: string }> {
    let path: string =
      `lmdb-admin/stores/${encodeURIComponent(storeId)}/databases/${encodeURIComponent(dbName)}/key?key=${encodeURIComponent(key)}`;
    path = this.withAriShardQuery(storeId, path);
    const req = this.deleteRequest<{ deleted: boolean; error?: string }>(path);
    if (!req) throw new Error('No server selected');
    return req;
  }

  deleteAllKeys(storeId: string, dbName: string): Observable<{ deleted: number; error?: string }> {
    let path: string =
      `lmdb-admin/stores/${encodeURIComponent(storeId)}/databases/${encodeURIComponent(dbName)}/keys`;
    path = this.withAriShardQuery(storeId, path);
    const req = this.deleteRequest<{ deleted: number; error?: string }>(path);
    if (!req) throw new Error('No server selected');
    return req;
  }

  clearSelection(): void {
    this.databases.set([]);
    this.keysResult.set({keys: [], total: 0});
    this.selectedValue.set(null);
    this.storeStats.set(null);
  }
}
